import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	ArrowLeft,
	HelpCircle,
	Loader2,
	Minus,
	MoreVertical,
	Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import useScrap from '../hooks/useScrap';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import useGetWishes from '../hooks/useGetWishes';
import useUpsertWish from '../hooks/useUpsertWish';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import WishRemoveModal from '../components/WishRemoveModal';
import useReactNative from '@/utils/useReactNative';

type ScrappedData = {
	image_url: string;
	name: string;
	price: number | null;
	url: string;
};

const urlParser = z.string().url();

// Ajoutez cette fonction en haut du fichier, hors du composant
const fixImageOrientation = (file: File): Promise<File> => {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				resolve(file);
				return;
			}

			// On définit les dimensions du canvas
			canvas.width = img.width;
			canvas.height = img.height;

			// On dessine l'image dans le canvas
			ctx.drawImage(img, 0, 0);

			// On convertit le canvas en blob
			canvas.toBlob((blob) => {
				if (!blob) {
					resolve(file);
					return;
				}
				// On crée un nouveau fichier avec le blob corrigé
				const correctedFile = new File([blob], file.name, {
					type: file.type,
					lastModified: file.lastModified,
				});
				resolve(correctedFile);
			}, file.type);
		};

		img.src = URL.createObjectURL(file);
	});
};

const WishForm = () => {
	const { t } = useTranslation();
	const { id_list, id_wish } = useParams();
	const navigate = useNavigate();

	const { wishes } = useGetWishes(parseInt(id_list as string));
	const wish = wishes?.find(
		(wish) => wish.id === parseInt(id_wish as string)
	);

	const [openAccordion, setOpenAccordion] = useState<'url' | 'detail'>('url');
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	const { scrap, scrapLoading } = useScrap();
	const { upsertWish, wishFormSchema } = useUpsertWish();

	const form = useForm<z.infer<typeof wishFormSchema>>({
		resolver: zodResolver(wishFormSchema),
		values: {
			url: wish?.url || '',
			name: wish?.name || '',
			comment: wish?.comment || '',
			image_url: wish?.image_key
				? `${import.meta.env.VITE_API_URL}/image?imageKey=${wish.image_key}`
				: '',
			scrapped: wish?.url ? true : false,
			price: (wish?.price?.toString() as unknown as number) || null,
		},
	});

	const scrapped = form.watch('scrapped');
	const image_url = form.watch('image_url');
	const url = form.watch('url');

	const handleUrlFieldChange = (url: string) => {
		if (!url) return;
		if (urlParser.safeParse(url).success === false) return;
		scrap(
			{ url },
			{
				onSuccess(response) {
					if (response.status !== 200) return;
					handleScrappedData(response.data);
				},
			}
		);
	};

	const { isReactNative, postMessage } = useReactNative();

	useEffect(() => {
		if (!isReactNative) return;
		postMessage({ message: 'CameraPermission' });
	}, []);

	useEffect(() => {
		if (!id_list || isNaN(parseInt(id_list))) {
			navigate('/home');
		}
		if (id_wish && wishes && wishes.length > 0 && !wish) {
			navigate('/home');
		}
	});

	if (!id_list) return null;
	if (id_wish && !wish) return null;

	const onSubmit = async (values: z.infer<typeof wishFormSchema>) => {
		const img = document.getElementById('image') as HTMLInputElement;
		const image = img?.files?.[0];
		upsertWish({
			wish: values,
			id_list: parseInt(id_list),
			image,
			currentWish: wish,
		});
		wish ? navigate(`/list/${id_list}/${wish.id}`) : navigate('../');
	};

	const handleScrappedData = (scrappedData: ScrappedData) => {
		form.setValue('name', scrappedData.name);
		form.setValue(
			'price',
			(scrappedData.price?.toString() as unknown as number) || null
		);
		form.setValue('image_url', scrappedData.image_url);
		form.setValue('scrapped', true);
		form.trigger('name');
		setOpenAccordion('detail');
	};

	const renderDropdown = () => {
		return (
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger asChild>
					<MoreVertical className="cursor-pointer" size={20} />
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					<DropdownMenuItem onClick={() => setOpenDeleteModal(true)}>
						<Minus className="mr-2 h-4 w-4" />
						<span>{t('wish.form.remove.menu')}</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	return (
		<div className="w-full">
			<h1 className="flex items-center gap-4 pb-5 text-xl font-bold">
				<ArrowLeft
					className="cursor-pointer"
					onClick={() => navigate('../')}
				/>
				<div className="flex items-center gap-2">
					{wish
						? t('wish.form.title.edit')
						: t('wish.form.title.create')}
					{wish && renderDropdown()}
				</div>
			</h1>
			<ScrollArea className="h-[calc(100%-20px)]">
				<Form {...form}>
					<form
						id="wishForm"
						onSubmit={form.handleSubmit(onSubmit)}
						className="grid gap-4"
					>
						<Accordion
							value={openAccordion}
							onValueChange={(value) =>
								setOpenAccordion(value as 'url' | 'detail')
							}
							type="single"
							collapsible
						>
							<AccordionItem value="url">
								<AccordionTrigger className="-mt-5">
									<div className="mt-4 flex items-center gap-2">
										<div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm text-primary-foreground">
											1
										</div>
										<span className="font-medium">
											{t('common.link')}
										</span>
										<HelpCircle size={16} />
									</div>
								</AccordionTrigger>
								<AccordionContent
									autoFocus={false}
									className="px-1"
								>
									<FormField
										control={form.control}
										name="url"
										render={({ field }) => (
											<FormItem>
												<Input
													id="url"
													type="url"
													label={t(
														'wish.form.url.label'
													)}
													placeholder={t(
														'wish.form.url.placeholder'
													)}
													{...field}
													onPaste={(e) => {
														handleUrlFieldChange(
															e.clipboardData.getData(
																'text'
															)
														);
													}}
													onInput={() => {
														if (scrapped) {
															form.setValue(
																'scrapped',
																false
															);
														}
													}}
													disabled={scrapLoading}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
									{scrapLoading && (
										<p className="flex items-center pt-2">
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t('wish.form.scrap.loading')}
										</p>
									)}
									<div className="flex justify-end gap-2 pt-4">
										<Button
											type="button"
											variant="ghost"
											disabled={scrapLoading}
											onClick={() =>
												setOpenAccordion('detail')
											}
										>
											{t('common.skip')}
										</Button>
										<Button
											variant="default"
											type="button"
											disabled={
												!url ||
												!urlParser.safeParse(url)
													.success ||
												scrapLoading
											}
											onClick={() => {
												if (!scrapped && url) {
													handleUrlFieldChange(url);
												} else {
													setOpenAccordion('detail');
												}
											}}
										>
											{t('common.next')}
										</Button>
									</div>
								</AccordionContent>
							</AccordionItem>
							<AccordionItem disabled={!scrapped} value="detail">
								<AccordionTrigger>
									<div className="mt-4 flex items-center gap-2">
										<div className="flex h-6 w-6  items-center justify-center rounded-full bg-primary text-sm text-primary-foreground">
											2
										</div>
										<span className="font-medium">
											{t('wish.form.details')}
										</span>
									</div>
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-4 px-1">
									<FormField
										control={form.control}
										name="name"
										render={({ field }) => (
											<FormItem>
												<Input
													id="name"
													label={t(
														'wish.form.name.label'
													)}
													placeholder={t(
														'wish.form.name.placeholder'
													)}
													{...field}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="price"
										render={({ field }) => (
											<FormItem>
												<Input
													id="price"
													inputMode="numeric"
													label={t(
														'wish.form.price.label'
													)}
													placeholder={t(
														'wish.form.price.placeholder'
													)}
													{...field}
													value={field.value || ''}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="comment"
										render={({ field }) => (
											<FormItem>
												<Textarea
													id="comment"
													label={t(
														'wish.form.comment.label'
													)}
													placeholder={t(
														'wish.form.comment.placeholder'
													)}
													{...field}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
									<div className={image_url ? 'hidden' : ''}>
										<FormField
											control={form.control}
											name="image"
											render={({ field }) => (
												<FormItem>
													<Input
														id="image"
														type="file"
														label={t(
															'wish.form.image.label'
														)}
														accept="image/*"
														onClick={(e) => {
															if (isReactNative) {
																e.preventDefault();
																postMessage({
																	message:
																		'InputFile',
																});
															}
														}}
														onChange={async (e) => {
															if (
																e.target
																	.files &&
																e.target
																	.files[0]
															) {
																const file =
																	e.target
																		.files[0];
																const correctedFile =
																	await fixImageOrientation(
																		file
																	);

																// Mise à jour de la preview
																form.setValue(
																	'image_url',
																	URL.createObjectURL(
																		correctedFile
																	)
																);

																// On met à jour le champ avec le fichier corrigé
																const dataTransfer =
																	new DataTransfer();
																dataTransfer.items.add(
																	correctedFile
																);
																e.target.files =
																	dataTransfer.files;

																field.onChange(
																	e
																);
															}
														}}
													/>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div
										className={
											image_url
												? 'flex flex-col gap-4 sm:max-w-md'
												: 'hidden'
										}
									>
										<Label htmlFor="image">Image</Label>
										<Button
											type="button"
											className="z-10 -mb-14 h-10 w-10 self-end px-2 shadow-lg"
											variant="default"
											onClick={() => {
												form.setValue('image', null);
												(
													document.getElementById(
														'image'
													) as HTMLInputElement
												).value = '';

												form.setValue('image_url', '');
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
										<img
											id="img"
											className="w-full rounded-lg"
											src={image_url}
											alt={form.getValues('name') || ''}
										/>
									</div>
									<div className="flex justify-end gap-2 pt-4">
										<Button
											type="button"
											variant="ghost"
											onClick={() =>
												setOpenAccordion('url')
											}
										>
											{t('common.back')}
										</Button>
										<Button type="submit" variant="default">
											{wish
												? t('wish.form.edit')
												: t('wish.form.create')}
										</Button>
									</div>
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</form>
				</Form>
			</ScrollArea>

			{openDeleteModal && (
				<WishRemoveModal
					open={openDeleteModal}
					setOpen={setOpenDeleteModal}
				/>
			)}
		</div>
	);
};

export default WishForm;
